<template>
  <div class="s-text-skeleton" :style="{ alignItems }">
    <PSkeleton
      v-for="line of lines"
      :key="line"
      :height="size.height"
      :style="{
        marginTop: size.margin,
        marginBottom: size.margin,
        width: width ?? getRandomWidth(),
      }"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Text content loading skeleton with configurable size and line count.
 * Mimics text appearance with random or fixed widths matching font sizes.
 */
import { computed } from "vue";

import PSkeleton from "@/atoms/PSkeleton.vue";

const props = withDefaults(
  defineProps<{
    align?: "center" | "left" | "right";
    fontSize?:
      | "2xl"
      | "3xl"
      | "4xl"
      | "5xl"
      | "6xl"
      | "base"
      | "lg"
      | "sm"
      | "xl"
      | "xs";
    lines?: number;
    maxWidth?: number;
    minWidth?: number;
    width?: string;
  }>(),
  { lines: 1, fontSize: "base", minWidth: 85, align: "left" },
);

const alignItems = computed(() => {
  if (props.align === "right") return "flex-end";
  if (props.align === "center") return "center";
  return "flex-start";
});

function getRandomWidth() {
  if (props.maxWidth) {
    return `${
      Math.floor(Math.random() * (props.maxWidth - props.minWidth + 1)) +
      props.minWidth
    }px`;
  }
  return `${Math.round(props.minWidth + Math.random() * (100 - props.minWidth))}%`;
}

const size = computed(() => {
  // Copies the tailwind config, keep it in sync. Values are in rem
  const [fontSize, lineHeight] = (
    {
      xs: [0.75, 1],
      sm: [0.875, 1.25],
      base: [1, 1.5],
      lg: [1.125, 1.75],
      xl: [1.25, 1.75],
      "2xl": [1.5, 2],
      "3xl": [1.875, 2.34375],
      "4xl": [2.25, 2.5875],
      "5xl": [3, 3.3],
      "6xl": [3.75, 3.75],
    } as const
  )[props.fontSize];

  return {
    height: `${fontSize}rem`,
    margin: `${(lineHeight - fontSize) / 2}rem`,
  };
});
</script>

<style lang="postcss">
.s-text-skeleton {
  @apply flex w-full flex-col;
}
</style>
